import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { translations } from '../i18n/translations';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');
  const [textOverrides, setTextOverrides] = useState({});
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalMessage, setLoginModalMessage] = useState('');
  const loginSuccessCallback = useRef(null);

  // Fetch text overrides at app start (and when language changes)
  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/texts/overrides?language=${language}`)
      .then(res => { if (!cancelled) setTextOverrides(res.data || {}); })
      .catch(() => { if (!cancelled) setTextOverrides({}); });
    return () => { cancelled = true; };
  }, [language]);

  // Public hook so admin save action can refresh overrides without page reload
  const refreshTextOverrides = useCallback(async () => {
    try {
      const res = await api.get(`/admin/texts/overrides?language=${language}`);
      setTextOverrides(res.data || {});
    } catch {
      // ignore
    }
  }, [language]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get(`/auth/me`);
      setUser(response.data);
      setLanguage(response.data.language || 'nl');
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post(`/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setLanguage(userData.language || 'nl');
    return userData;
  };

  const register = async (data) => {
    const response = await api.post(`/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setLanguage(userData.language || 'nl');
    return userData;
  };

  const logout = async () => {
    // Best-effort: tell server to revoke refresh token. Failures are fine —
    // localStorage cleanup happens either way.
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const resendVerification = async () => {
    const res = await api.post('/auth/resend-verification');
    return res.data;
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Require auth: shows login modal if not logged in, returns true if already logged in
  const requireAuth = (message, onSuccess) => {
    if (user) return true;
    setLoginModalMessage(message || '');
    loginSuccessCallback.current = onSuccess || null;
    setLoginModalOpen(true);
    return false;
  };

  const handleLoginModalSuccess = () => {
    if (loginSuccessCallback.current) {
      loginSuccessCallback.current();
      loginSuccessCallback.current = null;
    }
  };

  const RTL_LANGUAGES = ['ar'];

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
  };

  // Set initial lang + dir attribute
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
  }, [language]);

  // Translations — overrides win over translations.js defaults.
  // Optionele params dict voor `{placeholder}` interpolatie:
  //   t('filter.show_n_results', { count: 14 }) → "Toon 14 resultaten"
  const t = useCallback((key, params) => {
    let template;
    if (textOverrides[key]) template = textOverrides[key];
    else {
      const translation = translations[key];
      if (!translation) return key;
      template = translation[language] || translation['nl'] || key;
    }
    if (params && typeof template === 'string') {
      return template.replace(/\{(\w+)\}/g, (m, k) => (params[k] !== undefined ? String(params[k]) : m));
    }
    return template;
  }, [language, textOverrides]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      language,
      isRTL: RTL_LANGUAGES.includes(language),
      login,
      register,
      logout,
      resendVerification,
      updateUser,
      changeLanguage,
      t,
      refreshTextOverrides,
      requireAuth,
      loginModalOpen,
      setLoginModalOpen,
      loginModalMessage,
      handleLoginModalSuccess,
      isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
      isSuperAdmin: user?.role === 'superadmin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
